import { axios } from "../config/axios";
import { IAxiosResponse } from "../interfaces/IAxiosResponse";
import { IPagination } from "../interfaces/IPagination";
import {
  IInvoiceCancelTicketBody,
  IInvoiceCancelTicketResponse,
  IMarkSentResponse,
  IOperatorInvoice,
  IOperatorInvoicePreview,
  IOperatorInvoiceRange,
  OperatorInvoiceStatus
} from "../interfaces/endpoints/IOperatorInvoice";
import { getToken } from "../utils/auth/getToken";
import { serialize } from "../utils/serializeToQueryString";

const BASE = "/v2.0/OperatorInvoice";

export type GetPreviewParamsType = {
  operatorId: number;
  from: string;
  to: string;
};

export type GetInvoicesParamsType = {
  operatorId?: number;
  statusId?: OperatorInvoiceStatus;
  from?: string;
  to?: string;
  pageIndex: number;
  pageSize: number;
};

export const getOperatorInvoicePreview = async (
  params: GetPreviewParamsType
): Promise<IAxiosResponse<IOperatorInvoicePreview>> => {
  return axios.get(`${BASE}/preview${serialize(params)}`);
};

export const generateOperatorInvoice = async (
  body: IOperatorInvoiceRange
): Promise<IAxiosResponse<IOperatorInvoice>> => {
  return axios.post(`${BASE}/generate`, body);
};

export const getOperatorInvoices = async (
  params: GetInvoicesParamsType
): Promise<IAxiosResponse<IPagination<IOperatorInvoice>>> => {
  return axios.get(`${BASE}${serialize(params)}`);
};

export const getOperatorInvoiceById = async (
  id: number
): Promise<IAxiosResponse<IOperatorInvoice>> => {
  return axios.get(`${BASE}/${id}`);
};

export const downloadOperatorInvoiceFile = async (
  id: number
): Promise<{ blob: Blob; filename: string }> => {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `${token.token_type} ${token.access_token}`;
  }
  const base = (axios.defaults.baseURL ?? "").replace(/\/+$/, "");
  const res = await fetch(`${base}${BASE}/${id}/download`, { headers });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Download failed (${res.status})`);
  }
  const blob = await res.blob();
  const disp = res.headers.get("Content-Disposition") ?? "";
  const match = /filename\*?=(?:UTF-8'')?"?([^;"]+)"?/i.exec(disp);
  const filename = match
    ? decodeURIComponent(match[1])
    : `operator-invoice-${id}.xlsx`;
  return { blob, filename };
};

export const markOperatorInvoiceSent = async (
  id: number
): Promise<IAxiosResponse<IMarkSentResponse>> => {
  return axios.post(`${BASE}/${id}/mark-sent`);
};

export const cancelOperatorInvoice = async (
  id: number
): Promise<IAxiosResponse<IOperatorInvoice>> => {
  return axios.post(`${BASE}/${id}/cancel`);
};

export const updateOperatorInvoiceDocSheet = async (
  id: number
): Promise<IAxiosResponse<boolean>> => {
  return axios.post(`${BASE}/${id}/update-doc-sheet`);
};

export const TRACKER_SHEET_URL =
  "https://docs.google.com/spreadsheets/d/1WoIIcxyeNITGAnnjAhW8JV7ifkTmGgqj9z6BErk8gBg/edit";

export const invoiceCancelTicket = async (
  ticketId: number,
  body: IInvoiceCancelTicketBody
): Promise<IAxiosResponse<IInvoiceCancelTicketResponse>> => {
  return axios.post(`/Reservation/ticket/${ticketId}/invoice-cancel`, body);
};

export const undoInvoiceCancelTicket = async (
  cancellationId: number
): Promise<IAxiosResponse<unknown>> => {
  return axios.post(
    `/Reservation/ticket/invoice-cancel/${cancellationId}/undo`
  );
};
